import { Fragment, type ReactNode } from 'react';
import type { AulaEvento, ProgrammaEvento, SessioneEvento } from '@eventi/tipi.ts';
import { grigliaDelGiorno } from '../../lib/eventi/griglia';

type Props = {
  programma: ProgrammaEvento;
  giorno: string;
  /** Quali sessioni mettere in tabella (es. solo quelle da scegliere). */
  includi?: (s: SessioneEvento) => boolean;
  /** La prima colonna: il nome dell'aula e quello che serve accanto. */
  intestazioneAula: (aula: AulaEvento) => ReactNode;
  cella: (s: SessioneEvento) => ReactNode;
  vuoto?: string;
};

/** Larghezza minima della colonna delle aule e di ogni turno: sotto, la tabella scorre di lato. */
const REM_AULA = 10;
const REM_TURNO = 11;

/**
 * Un giorno del programma come orario: le aule in riga, i turni in colonna.
 * Su schermi stretti la tabella scorre di lato e la colonna delle aule resta
 * ferma, cosi' si sa sempre in che aula si e'.
 */
export function GrigliaGiorno({ programma, giorno, includi, intestazioneAula, cella, vuoto }: Props) {
  const righe = grigliaDelGiorno(programma, giorno, includi);
  if (righe.length === 0) {
    return <p className="py-8 text-center text-sm text-neutral-500">{vuoto ?? 'Nessun incontro in questo giorno.'}</p>;
  }

  const turni = programma.turni;
  // La prima riga della griglia e' l'intestazione dei turni: le aule partono dalla seconda.
  const partenze = righe.reduce<number[]>((acc, _r, i) => [...acc, i === 0 ? 2 : acc[i - 1] + righe[i - 1].corsie.length], []);

  return (
    <div className="overflow-x-auto overscroll-x-contain -mx-4 px-4 md:-mx-5 md:px-5 pb-1">
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `minmax(${REM_AULA}rem, 12rem) repeat(${turni.length}, minmax(${REM_TURNO}rem, 1fr))`,
          minWidth: `${REM_AULA + turni.length * REM_TURNO + turni.length * 0.5}rem`,
        }}
      >
        <div className="sticky left-0 z-10 bg-white" style={{ gridRow: 1, gridColumn: 1 }} />
        {turni.map((t, i) => (
          <div key={t.n} style={{ gridRow: 1, gridColumn: i + 2 }} className="px-1 pb-1">
            <span className="block text-xs font-semibold text-gray-900">Turno {t.n}</span>
            <span className="block text-[11px] text-neutral-500 tabular-nums">
              {t.inizio}–{t.fine}
            </span>
          </div>
        ))}

        {righe.map((r, i) => {
          const partenza = partenze[i];
          return (
            <Fragment key={r.aula.codice}>
              <div
                className="sticky left-0 z-10 bg-white pr-1"
                style={{ gridRow: `${partenza} / span ${r.corsie.length}`, gridColumn: 1 }}
              >
                {intestazioneAula(r.aula)}
              </div>
              {r.corsie.map((corsia, c) => {
                const occupate = new Set(corsia.flatMap((x) => Array.from({ length: x.colonne }, (_, k) => x.colonna + k)));
                return (
                  <Fragment key={c}>
                    {turni.map((t, colonna) =>
                      occupate.has(colonna) ? null : (
                        <div
                          key={`vuota-${t.n}`}
                          aria-hidden
                          style={{ gridRow: partenza + c, gridColumn: colonna + 2 }}
                          className="min-h-[4.5rem] rounded-lg border border-dashed border-gray-200 bg-gray-50/60"
                        />
                      ),
                    )}
                    {corsia.map((x) => (
                      <div
                        key={x.sessione.codice}
                        className="min-w-0"
                        style={{ gridRow: partenza + c, gridColumn: `${x.colonna + 2} / span ${x.colonne}` }}
                      >
                        {cella(x.sessione)}
                      </div>
                    ))}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
